import './App.css';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { StatsigProvider, useClientAsyncInit } from '@statsig/react-bindings';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';
import { StatsigSessionReplayPlugin } from '@statsig/session-replay';
import ThemeProvider from './components/theme-provider';
import { ColorSchemeProvider } from './contexts/color-scheme-context';
import { ColorSchemeUserSettingSync } from './components/color-scheme-user-setting-sync';
import { HostEnvironment } from './components/host-environment';
import { RouteTitleAndPageView } from './components/route-title-and-page-view';
import { CookieConsentProvider } from './contexts/cookie-consent-context';
import { SessionProvider } from './contexts/session-context';
import { AppErrorBoundary } from './components/errors/app-error-boundary';
import { hubRoutes } from './routes/hub-routes';
import { getStatsigEnvironmentTier } from './util/statsig-environment';
import { consumeGoogleSsoReturn } from './services/google-sso';

/**
 * UX-802 / UX-803 / API-506 — the Google sign-in and sign-up return (full contract in
 * `services/google-sso.ts`).
 *
 * The API redirects back to `/ux/login` (sign in) or `/ux/register` (sign up) with the result in
 * the URL FRAGMENT — `#sso=<outcome>&ssoNonce=<n>`, plus `&SessionKey=<guid>` for `success` and
 * `created`. `consumeGoogleSsoReturn()`
 * reads it, removes this tab's stored nonce, and accepts the outcome only when the nonces match
 * exactly; otherwise the outcome is `failed` and the SessionKey is discarded unpersisted (login
 * CSRF: a crafted link carries a nonce this tab never issued). The fragment is stripped either way.
 *
 * At module scope and BEFORE `createBrowserRouter`: the router snapshots `window.location` when it
 * is created and does not observe `replaceState`, so stripping later would leave a session id in
 * the router's own location.
 *
 * A matched `success` / `created` persists the SessionKey to the cookie + localStorage, which is
 * where `authorize()` looks when `HubLoginPage` / `HubRegisterPage` mounts — so the page's existing
 * boot authorize picks it up and redirects to `STUDIO_HOME` without the form ever rendering.
 *
 * ★ THE HUB DOES NOT CALL `consumeSessionKeyFromUrl()`. A bare `?SessionKey=` on a hub URL is NOT
 * accepted, deliberately — the nonce-checked fragment above is the only way a session arrives here.
 * The studio shell still accepts the query parameter for the impersonation spawn; that is its own,
 * unchanged behaviour and no longer something the hub mirrors.
 */
consumeGoogleSsoReturn();

const hubRouter = createBrowserRouter(hubRoutes);

export default function HubApp() {
    const id = 'a-user';
    const { client } = useClientAsyncInit(
        'client-zn4nxv2Wrig9nb4i5Y69vCCWdvYu4ZTzl4LsW0AWgnd',
        { userID: id },
        {
            environment: { tier: getStatsigEnvironmentTier() },
            plugins: [new StatsigAutoCapturePlugin(), new StatsigSessionReplayPlugin()]
        }
    );

    /*
     * No `loadingComponent` below: omitting it makes StatsigProvider render its children straight
     * away instead of gating them on `loadingStatus === 'Ready'`. It used to hold the whole app back
     * behind a bare `<div>Loading...</div>` - unstyled, so it flashed in the top-left corner on every
     * cold load. Nothing in the app reads a gate, experiment or dynamic config; Statsig is here for
     * `logEvent`, autocapture and session replay only, all of which the client queues before it is
     * ready. So there is nothing to wait for. Same change as `studio-app.tsx` and `delivery-app.tsx`.
     */
    return (
        <StatsigProvider client={client}>
            <AppErrorBoundary>
                <ColorSchemeProvider>
                    <ThemeProvider>
                        <CookieConsentProvider>
                            <SessionProvider>
                                <ColorSchemeUserSettingSync />
                                <HostEnvironment />
                                <RouteTitleAndPageView router={hubRouter} />
                                <RouterProvider router={hubRouter} />
                            </SessionProvider>
                        </CookieConsentProvider>
                    </ThemeProvider>
                </ColorSchemeProvider>
            </AppErrorBoundary>
        </StatsigProvider>
    );
}
